import axios from "axios";
import { refreshAccessToken, isRefreshTokenValid, clearAuthState } from '../utils/tokenUtils';

const API_BASE_URL =
  process.env.REACT_APP_API_URL || "https://riskreport.almata.co.jp:3400/api";

class ApiClient {
  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      timeout: 30000,
      headers: {
        "Content-Type": "application/json",
      },
    });

    this.authErrorShown = false;
    this.setupInterceptors();
  }

  setupInterceptors() {
    // Request interceptor
    this.client.interceptors.request.use(
      (config) => {
        const token = sessionStorage.getItem("authToken");
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor with token refresh
    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        
        // Handle account status issues
        if (error.response?.status === 403 && 
            error.response?.data?.message?.includes('inactive')) {
          this.handleAccountInactive();
          return Promise.reject(error);
        }
        
        // Handle unverified account
        if (error.response?.status === 401 && 
            error.response?.data?.message?.includes('verify your account')) {
          this.handleAccountUnverified(originalRequest.url?.includes('/login'));
          return Promise.reject(error);
        }
        
        // Handle token expiration with refresh attempt
        if (error.response?.status === 401 && 
            error.response?.data?.code === 'TOKEN_EXPIRED' && 
            !originalRequest._retry) {
          originalRequest._retry = true;
          
          try {
            if (isRefreshTokenValid()) {
              const newToken = await refreshAccessToken();
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              return this.client(originalRequest);
            }
          } catch (refreshError) {
            console.error('Token refresh failed:', refreshError);
            this.handleAuthError(error);
            return Promise.reject(error);
          }
        }
        
        // Handle other 401 errors (only if not already handled above and not a login request)
        if (error.response?.status === 401 && 
            !error.response?.data?.message?.includes('verify your account') &&
            !originalRequest.url?.includes('/login')) {
          if (!this.authErrorShown) {
            this.authErrorShown = true;
            this.handleAuthError(error);
          }
        }
        
        return Promise.reject(error);
      }
    );
  }

  handleAuthError(error) {
    const status = error.response?.status;
    const message = error.response?.data?.message;

    if (
      (status === 401 || status === 403) &&
      sessionStorage.getItem("authToken")
    ) {
      if (
        message?.includes("token") ||
        message?.includes("expired") ||
        message?.includes("Invalid")
      ) {
        clearAuthState();
        
        // Import toast dynamically to avoid circular dependency
        import('react-toastify').then(({ toast }) => {
          toast.error('Session expired. Please login again.');
        });
        
        setTimeout(() => {
          this.redirectToLogin();
        }, 1000);
      }
    }
  }

  handleAccountInactive() {
    // Don't clear auth state or redirect - let the login saga handle the error
    // User is already on login page, just show the error message
  }

  handleAccountUnverified(isLoginRequest = false) {
    clearAuthState();
    // Only redirect if not already on login page
    if (!isLoginRequest) {
      setTimeout(() => {
        window.location.href = "/login";
      }, 2000);
    }
  }

  // Deprecated: Use clearAuthState from tokenUtils instead
  clearAuthState() {
    clearAuthState();
  }

  redirectToLogin() {
    if (window.__REDUX_STORE__) {
      window.__REDUX_STORE__.dispatch({ type: "TOKEN_EXPIRED_SAGA" });
    } else {
      window.location.href = "/login";
    }
  }

  // Standard HTTP methods
  get(url, config = {}) {
    return this.client.get(url, config);
  }

  post(url, data = {}, config = {}) {
    return this.client.post(url, data, config);
  }

  put(url, data = {}, config = {}) {
    return this.client.put(url, data, config);
  }
  patch(url, data = {}, config = {}) {
    return this.client.patch(url, data, config);
  }

  delete(url, config = {}) {
    return this.client.delete(url, config);
  }

  // Form data methods
  postFormData(url, formData, config = {}) {
    return this.client.post(url, formData, {
      ...config,
      headers: {
        ...config.headers,
        "Content-Type": "multipart/form-data",
      },
    });
  }

  putFormData(url, formData, config = {}) {
    return this.client.patch(url, formData, {
      ...config,
      headers: {
        ...config.headers,
        "Content-Type": "multipart/form-data",
      },
    });
  }
}

const apiClient = new ApiClient();

// Store reference for Redux integration
export const setStore = (storeInstance) => {
  window.__REDUX_STORE__ = storeInstance;
};

export { apiClient as default };